import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { FadeUp } from "./Reveal";
import { BOOKING_LINKS } from "../config";

const plans = [
  {
    tag: "Self-Service",
    title: "In-Store Machines",
    items: [
      ["Standard Washers", "from $3.00 / load"],
      ["Large-Capacity Washers", "from $4.00 / load"],
      ["High-Efficiency Dryers", "$0.25 / 6 min"],
    ],
    foot: "Detergent & laundry supplies available in store",
    cta: "Plan Your Visit",
    href: "#contact",
    testid: "pricing-self-service-cta",
  },
  {
    tag: "Most Popular",
    title: "Wash, Dry & Fold",
    items: [
      ["Everyday Laundry", "$1.50 / lb"],
      ["Bedding & Bulky Items", "$25 & Up"],
      ["Minimum Order", "10 lbs"],
    ],
    foot: "Next-day turnaround included",
    featured: true,
  },
  {
    tag: "Doorstep Service",
    title: "Pickup & Delivery",
    items: [
      ["Wash, Dry & Fold, Delivered", "$2.49 / lb"],
      ["Delivery Fee", "$4.99"],
      ["Minimum Order", "25 lbs"],
    ],
    cta: "Schedule a Pickup",
    href: BOOKING_LINKS.pickupDelivery,
    external: true,
    testid: "pricing-pickup-cta",
  },
];

const Card = ({ plan, index }) => {
  const featured = plan.featured;
  return (
    <FadeUp
      delay={index * 0.12}
      data-testid={`pricing-card-${index + 1}`}
      className={`relative flex flex-col rounded-[24px] border p-8 transition-transform duration-500 hover:-translate-y-1.5 lg:p-10 ${
        featured
          ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#F9F6F0] shadow-[0_16px_48px_rgba(26,26,26,0.18)]"
          : "border-[#E2DFD8]/60 bg-white/70 text-[#1A1A1A] shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-md"
      }`}
    >
      <span
        className={`inline-flex w-fit items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] ${
          featured
            ? "bg-[#C85C40] text-white"
            : "border border-[#E2DFD8] text-[#5C5C5A]"
        }`}
      >
        {featured && <BadgeCheck className="h-3.5 w-3.5" />}
        {plan.tag}
      </span>

      <h3 className="mt-6 font-serif text-3xl tracking-tight sm:text-4xl">
        {plan.title}
      </h3>

      <ul className="mt-8 flex flex-col gap-4">
        {plan.items.map(([label, price]) => (
          <li key={label} className="flex items-baseline gap-3">
            <span
              className={`text-sm font-semibold ${
                featured ? "text-[#F9F6F0]/80" : "text-[#5C5C5A]"
              }`}
            >
              {label}
            </span>
            <span
              aria-hidden="true"
              className={`grow border-b border-dotted ${
                featured ? "border-white/20" : "border-[#E2DFD8]"
              }`}
            />
            <span className="whitespace-nowrap font-serif text-base font-bold tracking-tight">
              {price}
            </span>
          </li>
        ))}
      </ul>

      {plan.foot && (
        <p
          className={`mt-6 text-xs ${
            featured ? "text-[#F9F6F0]/50" : "text-[#5C5C5A]/70"
          }`}
        >
          {plan.foot}
        </p>
      )}

      {plan.cta && (
        <a
          href={plan.href}
          {...(plan.external
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
          data-testid={plan.testid}
          className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-[background-color,transform] duration-300 hover:scale-[0.98] ${
            featured
              ? "bg-[#C85C40] text-white hover:bg-[#A84C34]"
              : "bg-[#1A1A1A] text-[#F9F6F0] hover:bg-[#C85C40]"
          }`}
        >
          {plan.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      )}
    </FadeUp>
  );
};

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="scroll-mt-28 bg-[#EFEAE1]/60 py-32 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#C85C40]">
            Pricing
          </p>
          <h2 className="mt-4 font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
            Simple, honest{" "}
            <span className="italic text-[#C85C40]">pricing</span>.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5C5C5A] md:text-lg">
            No surprises, no fine print — just fresh laundry at a fair price.
          </p>
        </FadeUp>

        <div className="mt-20 grid gap-6 lg:grid-cols-3 lg:gap-8">
          {plans.map((p, i) => (
            <Card key={p.title} plan={p} index={i} />
          ))}
        </div>

        <FadeUp delay={0.2}>
          <p
            className="mt-10 text-center text-xs text-[#5C5C5A]/70"
            data-testid="pricing-disclaimer"
          >
            Grand-opening rates — final pricing will be posted in store.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}
